import { type Timezone, timezones } from '@ipfabric/utils/timezones';
import { z } from 'zod';

export type CronFormMeta = {
  dirty: boolean;
  valid: boolean;
};

export type CronEveryOption =
  | 'year'
  | 'month'
  | 'week'
  | 'day'
  | 'hour'
  | 'custom';

/**
 * State structure for each option type
 */
export type YearOptionState = {
  months: string[];
  days: string[];
  hour: string;
  minute: string;
};

export type MonthOptionState = {
  days: string[];
  hour: string;
  minute: string;
};

export type WeekOptionState = {
  weekDays: string[];
  hour: string;
  minute: string;
};

export type DayOptionState = {
  hour: string;
  minute: string;
};

export type HourOptionState = {
  minute: string;
};

export type CustomOptionState = {
  minute: string;
  hour: string;
  days: string;
  months: string;
  weekDays: string;
};

export type CronOptionStates = {
  year: YearOptionState;
  month: MonthOptionState;
  week: WeekOptionState;
  day: DayOptionState;
  hour: HourOptionState;
  custom: CustomOptionState;
};

const timezoneSchema = z
  .string()
  .refine(
    (value): value is Timezone => timezones.includes(value as Timezone),
    'Invalid timezone',
  );

const cronFormSchemaObject = z.object({
  every: z.enum(['year', 'month', 'week', 'day', 'hour', 'custom']),
  months: z.union([z.array(z.string()), z.string()]),
  weekDays: z.union([z.array(z.string()), z.string()]),
  days: z.union([z.array(z.string()), z.string()]),
  hour: z.string().min(1, ''),
  minute: z.string().min(1, ''),
  timezone: timezoneSchema,
});

export type CronFormData = z.infer<typeof cronFormSchemaObject>;

export { cronFormSchemaObject };
